/**
 * Shared pipe to transform date in fantaz app and convert date using timezone
 * and language code.
 *
 * @param value The date expression: a `Date` object,  a number
 * (milliseconds since UTC epoch), or an ISO string (https://www.w3.org/TR/NOTE-datetime).
 * @param format The date/time components to include, using predefined options or a
 * custom format string.
 * @usageNotes
 *
 * <span>{{ date | fzDate }}</span>
 * <span>{{ date | fzDate:'small' }}</span>
 *
 */
import { Pipe, PipeTransform } from '@angular/core';

import { catchError, map, merge, Observable, of } from 'rxjs';

import { TrackError } from '@fantaz/core/utils';
import { getTimeZoneAbbr } from '@fantaz/shared/data-access/api/timezones';
import { UIStore } from '@fantaz/shared/data-access/store';
import { formatInTimeZone } from '@fantaz/shared/utils/date';

const formatMap: Record<string, string> = {
  small: 'MMM d, y', //
  medium: 'MMM d, y, h:mm:ss a', // Jun 15, 2015, 9:03:01 AM
  mediumDate: 'MMM d, y', // Jun 15, 2015
  long: 'E, MMM d, y, h:mm:ss', // Sat, Jun 15, 2015, 9:03:01 AM
  local: 'MMM d, y, h:mm a', //Jun 15, 2015, 12:00 AM (EST)
};

@Pipe({
  name: 'fzDateFormat',
})
export class SharedDatePipe implements PipeTransform {
  constructor(private readonly uiStore: UIStore) {}

  transform(
    value: string | number | Date | null | undefined,
    ...args: string[]
  ): Observable<string | null> {
    let format = formatMap['long']; // by default use long format

    if (args && args.length && args.length > 0) {
      if (formatMap[args[0]]) {
        format = formatMap[args[0]];
      } else {
        format = args[0];
      }
    }

    // check if formatted date need include timezone
    const suffix =
      format.includes('h') ||
      format.includes('H') ||
      format.includes('s') ||
      format.includes('m');

    return merge(this.uiStore.timezone$).pipe(
      map((timezone) => {
        const d = formatInTimeZone(
          value as string | number | Date,
          timezone,
          format,
        );
        return `${d} ${suffix ? '(' + getTimeZoneAbbr(timezone) + ')' : ''}`;
      }),
      catchError(() => {
        TrackError.trackCustomError(
          'shared-date-pipe-error',
          JSON.stringify({ value, format }),
          'The shared date pipe is returning an error',
        );

        return of('');
      }),
    );
  }
}
